/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/FilesTab" />
import * as React from "react";
import { ChangesExplorerView } from "Repos/Changes/Components/ChangesExplorerView";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { GitVersionDescriptor, IterationReason } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { ObservableArray } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext, useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { IButtonProps } from "VSSUI/Button";
import { Dropdown, DropdownCallout, DropdownExpandableButton } from "VSSUI/Dropdown";
import { getKeywordFilterItem } from "VSSUI/Filter";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { IIteration } from "../../Contracts/PullRequest";
import { DiscussionFilterKeys, getDiscussionFilterService } from "../../Discussion/PullRequestDiscussionFilterService";
import { getDiscussionIntegrationService } from "../../Discussion/PullRequestDiscussionIntegrationService";
import { FilesFilterKeys, FilesFilterViewedKeys, getPullRequestViewService } from "../../PullRequestViewService";
import { getPullRequestDiscussionService } from "../../Discussion/PullRequestDiscussionThreadService"; 
import { renderSelectedFilterIdentityConditionally, renderSelectedFilterItemsConditionally } from "../../Util/Filter";

const viewedOptions: ObservableArray<IListBoxItem<boolean>> = new ObservableArray([
    { id: FilesFilterViewedKeys.all, text: Resources.FilesFilterAll },
    { id: FilesFilterViewedKeys.pending, text: Resources.FilesFilterPending },
    { id: FilesFilterViewedKeys.viewed, text: Resources.FilesFilterViewed }
]);

export function FilesTabContent() {
    const { pageContext } = useComponentContext();
    const viewService = getPullRequestViewService(pageContext);
    const prDetailsService = getPullRequestViewService(pageContext);
    const discussionService = getDiscussionIntegrationService(pageContext);
    const reposRoutingService = getReposRoutingService(pageContext);
    const filterStore = viewService.getFilesFilter();

    const discussionFilterService = getDiscussionFilterService(pageContext);
    const fileTypeOptions = discussionFilterService.getFileTypeOptions();
    const commentPreferenceOptions = discussionFilterService.getCommentPreferenceOptions();
    const commentStatusOptions = discussionFilterService.getCommentStatusOptions();
    const commentOwnerOptions = discussionFilterService.getCommentOwnerOptions();
    const discussionThreadService = getPullRequestDiscussionService(pageContext);

    const targetRepository = prDetailsService.getTargetRepository();
    const lastMergeCommit = prDetailsService.getLastMergeCommit();
    const mergeTargetCommit = prDetailsService.getMergeTargetCommit();
    const mergeChangesUrl =
        lastMergeCommit &&
        mergeTargetCommit &&
        reposRoutingService.getBranchCompareUrl(targetRepository.name, targetRepository.project.name, mergeTargetCommit, lastMergeCommit);

    const onViewChange = () => {
        const discussionThreads = discussionThreadService.getDiscussionThreads()
                                .value.filter(x => x.getExpanded().value === false);
        discussionThreadService.applyFilter(discussionThreads.filter(x => x.getVisible().value === false), false, true);
        discussionThreadService.applyFilter(discussionThreads.filter(x => x.getVisible().value === true), true, true);
    };

    return (
        <div className="flex-column flex-grow">
            <Observer changes={viewService.getChanges()} allFiles={viewService.getAllChangedPaths()}>
                {(props: { changes: IChangeCollection<GitVersionDescriptor>; allFiles: string[] }) => {
                    const filterProvider = new AggregateItemProvider<IListBoxItem<{}>>();

                    const pathParts = new Set<string>();
                    for (const path of props.allFiles) {
                        for (const part of path.split("/")) {
                            if (part.length > 0) {
                                pathParts.add(part);
                            }
                        }
                    }

                    const sortedParts = Array.from(pathParts).sort(localeIgnoreCaseComparer);
                    const files: IListBoxItem<boolean>[] = sortedParts.map(file => ({ id: file, text: file }));
                    filterProvider.push(files);
                    filterProvider.push(viewedOptions);
                    filterProvider.push(fileTypeOptions);
                    filterProvider.push(commentPreferenceOptions);
                    filterProvider.push(commentStatusOptions);
                    filterProvider.push(commentOwnerOptions);

                    const thereAreConflictResolutions = viewService.getIterations().value.filter(x => x.reason === IterationReason.ResolveConflicts).length > 0;
                    const selectedIteration = viewService.getIterations().value.filter(x => x.id === viewService.getSelectedIterationId().value)[0];
                    const isConflictResolutionIteration = selectedIteration && selectedIteration.reason === IterationReason.ResolveConflicts;

                    const conflictResolutionButtonProps: IButtonProps[] = [
                        {
                            text: Resources.ViewMergeChanges,
                            iconProps: { iconName: "BranchCommit" },
                            href: mergeChangesUrl
                        }
                    ];

                    return (
                        <>
                            {thereAreConflictResolutions && mergeChangesUrl && (
                                <MessageCard
                                    severity={MessageCardSeverity.Info}
                                    className="conflicts-resolved-message-card"
                                    buttonProps={conflictResolutionButtonProps}
                                >
                                    {Resources.ThereAreConflictResolutions}
                                </MessageCard>
                            )}
                            <ChangesExplorerView
                                addDiffKeyboardShortcuts={true}
                                changes={props.changes}
                                discussionService={discussionService}
                                filterProps={{
                                    filterStore,
                                    filterItems: [
                                        getKeywordFilterItem(filterStore, 400, files),
                                        {
                                            enforceSingleSelect: true,
                                            filterItemKey: FilesFilterKeys.viewedFile,
                                            id: "files-viewed-filter",
                                            items: viewedOptions.value,
                                            name: Resources.FilterReviewed,
                                            renderSelectedItems: (selectedItems: IListBoxItem<string>[]) =>
                                                renderSelectedFilterItemsConditionally(filterStore, FilesFilterKeys.viewedFile, selectedItems)
                                        },
                                        {
                                            enforceSingleSelect: true,
                                            filterItemKey: FilesFilterKeys.commentedFile,
                                            id: "files-commented-filter",
                                            items: fileTypeOptions.value,
                                            name: Resources.DiscussionFilterCommentedFiles,
                                            renderSelectedItems: (selectedItems: IListBoxItem<string>[]) =>
                                                renderSelectedFilterItemsConditionally(filterStore, FilesFilterKeys.commentedFile, selectedItems)
                                        },
                                        {
                                            enforceSingleSelect: true,
                                            filterItemKey: DiscussionFilterKeys.commentPreference,
                                            id: "comment-preference-filter",
                                            items: commentPreferenceOptions.value,
                                            name: Resources.DiscussionFilterCommentPreference,
                                            renderSelectedItems: (selectedItems: IListBoxItem<string>[]) =>
                                                renderSelectedFilterItemsConditionally(filterStore, DiscussionFilterKeys.commentPreference, selectedItems)
                                        },
                                        {
                                            filterItemKey: DiscussionFilterKeys.commentStatus,
                                            id: "comment-status-filter",
                                            items: commentStatusOptions.value,
                                            name: Resources.DiscussionFilterCommentStatus,
                                            renderSelectedItems: (selectedItems: IListBoxItem<string>[]) =>
                                                renderSelectedFilterItemsConditionally(filterStore, DiscussionFilterKeys.commentStatus, selectedItems)
                                        },
                                        {
                                            enforceSingleSelect: true,
                                            filterItemKey: DiscussionFilterKeys.commentOwner,
                                            id: "comment-owner-filter",
                                            items: commentOwnerOptions.value,
                                            name: Resources.DiscussionFilterCommentOwner,
                                            renderSelectedItems: (selectedItems: IListBoxItem<IdentityRef>[]) =>
                                                renderSelectedFilterIdentityConditionally(filterStore, DiscussionFilterKeys.commentOwner, selectedItems)
                                        }
                                    ],
                                    items: filterProvider,
                                    selection: new DropdownMultiSelection(),
                                    showActiveFilterResetButton: true,
                                    showFilterOnText: false
                                }}
                                getFileUrl={path => {
                                    const url = reposRoutingService.getPullRequestUrl(
                                        viewService.getTargetRepository().name,
                                        viewService.getTargetRepository().project.name,
                                        viewService.getId(),
                                        "files",
                                        path
                                    );
                                    return url;
                                }}
                                getHighlightText={() => filterStore.getFilterItemValue(FilesFilterKeys.keyword)}
                                getItemMenuItems={(item, discussionIntegration) => viewService.getItemMenuItems(item, discussionIntegration)}
                                onCheckmarkChange={(change, checkmarked) => viewService.setChangeViewed(change, checkmarked)}
                                preFilterContent={<PullRequestIterationDropdown />}
                                selectedThreadId={viewService.getSelectedThreadId()}
                                viewerWidgets={viewService.getViewWidgets()}
                                isConflictResolutionIteration={isConflictResolutionIteration}
                                conflictResolutionIterationLink={mergeChangesUrl}
                                onViewChange={onViewChange}
                            />
                        </>
                    );
                }}
            </Observer>
        </div>
    );
}

class PullRequestIterationDropdown extends VssComponent {
    private dropdownItems = new ObservableArray<IListBoxItem<IIteration>>();
    private selection = new DropdownMultiSelection();
    private lastIteration = 0;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.setIterationListBoxItems();
    }

    public render() {
        const viewService = getPullRequestViewService(this.context.pageContext);

        return viewService.getIterationsSupport() ? (
            <Observer
                iterations={{ observableValue: viewService.getIterations(), filter: this.setIterationListBoxItems }}
                selectedIteration={{ observableValue: viewService.getSelectedIterationId(), filter: this.setIterationListBoxItems }}
                baseIterationId={{ observableValue: viewService.getBaseIterationId(), filter: this.setIterationListBoxItems }}
            >
                {() => {
                    return (
                        <ScreenSizeObserver>
                            {(screenSizeProps: { screenSize: ScreenSize }) => (
                                <Dropdown
                                    items={this.dropdownItems}
                                    selection={this.selection}
                                    onSelect={this.onSelectIteration}
                                    renderCallout={props => {
                                        return (
                                            <DropdownCallout
                                                {...props}
                                                anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                                dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                                actions={
                                                    screenSizeProps.screenSize <= ScreenSize.xsmall
                                                        ? []
                                                        : [
                                                              {
                                                                  className: "repos-pr-iteration-picker-instructions",
                                                                  excludeFocusZone: true,
                                                                  excludeTabStop: true,
                                                                  text: Resources.FilesDiffPickerBaseExplanation
                                                              }
                                                          ]
                                                }
                                                width={props.width ? Math.max(props.width, 400) : 400}
                                            />
                                        );
                                    }}
                                    renderExpandable={props => {
                                        return <DropdownExpandableButton {...props} renderSelectedItems={this.getSelectedIterationText} />;
                                    }}
                                />
                            )}
                        </ScreenSizeObserver>
                    );
                }}
            </Observer>
        ) : null;
    }

    private getSelectedIterationText = (): JSX.Element | string => {
        const selection = this.selection;

        if (!selection.value.length) {
            return "";
        }

        const beginIndex = selection.value[0].beginIndex;
        const endIndex = selection.value[0].endIndex + 1;
        if (beginIndex === 0) {
            return Resources.AllChanges;
        }

        const beginItem = this.dropdownItems.value[beginIndex];
        const endItem = this.dropdownItems.value[endIndex];

        if (beginItem) {
            const baseUpdate = endItem ? format(Resources.Update, endItem.id) : Resources.Base;
            const compareUpdate = format(Resources.Update, beginItem.id);
            return format("{0} → {1}", baseUpdate, compareUpdate);
        } else {
            return "";
        }
    };

    private onSelectIteration = (event: React.SyntheticEvent<HTMLElement, Event>, iteration: IListBoxItem<IIteration>) => {
        const viewService = getPullRequestViewService(this.context.pageContext);
        const currentIteration = viewService.getSelectedIterationId().value;

        if (!iteration.data) {
            // All changes item
            this.selection.select(0, 1, false);
            viewService.selectIteration(this.lastIteration, 0);
        } else {
            let selected = iteration.data.id;
            let base = iteration.data.id - 1;

            if (currentIteration !== selected && (event as any).shiftKey) {
                if (currentIteration < selected) {
                    base = Math.min(selected - 1, currentIteration - 1);
                } else {
                    selected = Math.max(selected, currentIteration);
                }
            }

            this.selection.select(this.lastIteration - selected + 2, selected - base, false);
            viewService.selectIteration(selected, base);
        }
    };

    private setIterationListBoxItems = (): void => {
        const viewService = getPullRequestViewService(this.context.pageContext);

        // Update the list of dropdown items
        const iterations = viewService.getIterations().value;
        const items: IListBoxItem<IIteration>[] = [];
        items.push({ id: "all-changes", text: Resources.AllChanges });
        items.push({ id: "all-changes-divider", type: ListBoxItemType.Divider });

        for (let i = iterations.length - 1; i >= 0; i--) {
            const iteration = iterations[i];
            items.push({
                id: iteration.id.toString(),
                data: iteration,
                render: (rowIndex, columnIndex, tableColumn, tableItem) => (
                    <SimpleTableCell contentClassName="rhythm-horizontal-4" columnIndex={columnIndex} key={tableItem.id} tableColumn={tableColumn}>
                        <div className="padding-horizontal-4 body-s">{iteration.id}</div>
                        <Tooltip text={iteration.description} overflowOnly={true}>
                            <div className="flex-grow text-ellipsis">{iteration.description}</div>
                        </Tooltip>
                        <div className="flex-noshrink secondary-text">
                            <Ago date={iteration.updatedDate} format={AgoFormat.Compact} />
                        </div>
                    </SimpleTableCell>
                ),
                tooltipProps: { disabled: true }
            });
        }

        this.lastIteration = iterations.length ? iterations[iterations.length - 1].id : 0;
        this.dropdownItems.value = items;

        // Update the selection
        const baseIteration = viewService.getBaseIterationId().value;
        const selectedIteration = viewService.getSelectedIterationId().value;

        if (baseIteration === 0 && selectedIteration === this.lastIteration) {
            this.selection.select(0, 1, false);
        } else {
            this.selection.select(this.lastIteration - selectedIteration + 2, selectedIteration - baseIteration, false);
        }
    };
}
